'use strict';

const SESSION_HEADER = 'X-SC-Tracking-Session-ID';
const ANONYMOUS_HEADER = 'X-SC-Anonymous-ID';
const REQUEST_ID_HEADER = 'X-Request-ID';
const STORAGE_KEY = 'shopchill.tracking_session_uuid.v1';
const ANONYMOUS_STORAGE_KEY = 'shopchill.anonymous_uuid.v1';
const SCHEMA_VERSION = '1.0';
const ALLOWED_QUERY_PARAMETERS = new Set([
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'fbclid',
    'ttclid',
]);
const SENSITIVE_KEYS = /^(access_token|api_token|authorization|password|otp|card_number|cvv|cookie|session|email|phone|address)$/i;
const RETRYABLE_STATUSES = new Set([429, 500, 502, 503, 504]);
const SUCCESS_STATUSES = new Set([
    'accepted',
    'accepted_duplicate',
    'accepted_noop',
    'disabled',
]);

function isUuidV4(value) {
    return typeof value === 'string'
        && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function defaultUuidGenerator() {
    if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }

    if (globalThis.crypto && typeof globalThis.crypto.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        globalThis.crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));

        return [
            hex.slice(0, 4).join(''),
            hex.slice(4, 6).join(''),
            hex.slice(6, 8).join(''),
            hex.slice(8, 10).join(''),
            hex.slice(10, 16).join(''),
        ].join('-');
    }

    throw new Error('A cryptographically secure UUID generator is required.');
}

function sanitizeUrl(value, baseUrl) {
    const parsed = new URL(String(value), baseUrl);
    const sanitized = new URL(parsed.origin + parsed.pathname);

    parsed.searchParams.forEach((parameterValue, key) => {
        if (ALLOWED_QUERY_PARAMETERS.has(key)) {
            sanitized.searchParams.append(key, parameterValue.slice(0, 190));
        }
    });

    const result = sanitized.toString();
    if (result.length > 2048) {
        throw new Error('Tracking URL exceeds 2048 characters.');
    }

    return result;
}

function sanitizeFilters(filters) {
    if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
        return undefined;
    }

    const sanitized = {};
    Object.entries(filters).slice(0, 10).forEach(([key, value]) => {
        if (
            !SENSITIVE_KEYS.test(key)
            && ['string', 'number', 'boolean'].includes(typeof value)
        ) {
            sanitized[String(key).slice(0, 50)] = String(value).slice(0, 100);
        }
    });

    return Object.keys(sanitized).length ? sanitized : undefined;
}

function compact(object) {
    return Object.fromEntries(
        Object.entries(object).filter(([, value]) => value !== undefined && value !== null)
    );
}

function normalizeSearchKeyword(value) {
    const normalized = String(value || '').trim().replace(/\s+/gu, ' ');

    return Array.from(normalized).slice(0, 200).join('');
}

class WebTrackingSessionStorage {
    constructor(storage, uuidGenerator = defaultUuidGenerator, key = STORAGE_KEY) {
        this.storage = storage;
        this.uuidGenerator = uuidGenerator;
        this.key = key;
    }

    read() {
        try {
            const value = this.storage.getItem(this.key);

            return isUuidV4(value) ? value.toLowerCase() : null;
        } catch (error) {
            return null;
        }
    }

    write(value) {
        if (!isUuidV4(value)) {
            return false;
        }

        try {
            this.storage.setItem(this.key, value.toLowerCase());

            return this.read() === value.toLowerCase();
        } catch (error) {
            return false;
        }
    }

    getOrCreate() {
        const existing = this.read();
        if (existing) {
            return existing;
        }

        const generated = this.uuidGenerator().toLowerCase();
        if (!isUuidV4(generated)) {
            throw new Error('UUID generator did not return UUID v4.');
        }

        this.write(generated);

        return this.read() || generated;
    }
}

class TrackingTransport {
    constructor({
        endpoint = '/tracking/events',
        fetchImplementation = globalThis.fetch,
        sessionStorage,
        anonymousStorage,
        timeoutMs = 2500,
        maxRetries = 1,
        retryDelayMs = 250,
        sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)),
        headersProvider = () => ({}),
    }) {
        if (typeof fetchImplementation !== 'function') {
            throw new Error('Tracking transport requires fetch.');
        }

        this.endpoint = endpoint;
        this.fetchImplementation = fetchImplementation;
        this.sessionStorage = sessionStorage;
        this.anonymousStorage = anonymousStorage;
        this.timeoutMs = timeoutMs;
        this.maxRetries = maxRetries;
        this.retryDelayMs = retryDelayMs;
        this.sleep = sleep;
        this.headersProvider = headersProvider;
    }

    async send(payload) {
        let attempt = 0;

        while (attempt <= this.maxRetries) {
            try {
                const trackingSessionUuid = this.sessionStorage.getOrCreate();
                const anonymousUuid = this.anonymousStorage.getOrCreate();
                const response = await this.request(payload, trackingSessionUuid, anonymousUuid);
                this.captureSession(response);
                const body = await this.responseBody(response);
                const contractSuccess = SUCCESS_STATUSES.has(body.status);

                if (
                    RETRYABLE_STATUSES.has(response.status)
                    && !contractSuccess
                    && attempt < this.maxRetries
                ) {
                    await this.sleep(this.retryDelay(response, attempt));
                    attempt += 1;
                    continue;
                }

                return {
                    ok: contractSuccess,
                    httpStatus: response.status,
                    status: body.status || 'unknown',
                    duplicate: body.duplicate === true,
                    eventUuid: body.event_uuid || null,
                    trackingSessionUuid: this.sessionStorage.read(),
                    retryable: RETRYABLE_STATUSES.has(response.status),
                };
            } catch (error) {
                if (attempt >= this.maxRetries) {
                    return {
                        ok: false,
                        httpStatus: 0,
                        status: error && error.name === 'AbortError'
                            ? 'timeout'
                            : 'network_failure',
                        retryable: true,
                    };
                }

                await this.sleep(this.retryDelayMs * (attempt + 1));
                attempt += 1;
            }
        }
    }

    async request(payload, trackingSessionUuid, anonymousUuid) {
        const controller = typeof AbortController === 'function'
            ? new AbortController()
            : null;
        const timeout = controller
            ? setTimeout(() => controller.abort(), this.timeoutMs)
            : null;

        try {
            return await this.fetchImplementation(this.endpoint, {
                method: 'POST',
                credentials: 'same-origin',
                keepalive: false,
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    [SESSION_HEADER]: trackingSessionUuid,
                    [ANONYMOUS_HEADER]: anonymousUuid,
                    ...this.headersProvider(),
                },
                body: JSON.stringify(payload),
                signal: controller ? controller.signal : undefined,
            });
        } finally {
            if (timeout) {
                clearTimeout(timeout);
            }
        }
    }

    captureSession(response) {
        const replacement = response.headers
            && typeof response.headers.get === 'function'
            ? response.headers.get(SESSION_HEADER)
            : null;

        if (isUuidV4(replacement)) {
            this.sessionStorage.write(replacement);
        }
    }

    async responseBody(response) {
        try {
            const body = await response.json();

            return body && typeof body === 'object' && !Array.isArray(body)
                ? body
                : {};
        } catch (error) {
            return {};
        }
    }

    retryDelay(response, attempt) {
        if (response.status === 429 && response.headers) {
            const retryAfter = Number(response.headers.get('Retry-After'));
            if (Number.isFinite(retryAfter) && retryAfter >= 0) {
                return Math.min(retryAfter * 1000, 5000);
            }
        }

        return this.retryDelayMs * (attempt + 1);
    }
}

class TrackingClient {
    constructor({
        transport,
        uuidGenerator = defaultUuidGenerator,
        clock = () => new Date(),
        locationProvider = () => globalThis.location && globalThis.location.href,
        referrerProvider = () => globalThis.document && globalThis.document.referrer,
        flags = {},
        source = 'web',
        context = {},
        debug = () => {},
        maxOccurrences = 500,
    }) {
        this.transport = transport;
        this.uuidGenerator = uuidGenerator;
        this.clock = clock;
        this.locationProvider = locationProvider;
        this.referrerProvider = referrerProvider;
        this.source = source;
        this.context = compact(context);
        this.debug = debug;
        this.maxOccurrences = maxOccurrences;
        this.occurrences = new Map();
        this.flags = {
            enabled: false,
            sessionPersistence: false,
            PageView: false,
            ViewCategory: false,
            ViewContent: false,
            Search: false,
            SelectCoupon: false,
            TrackOrder: false,
            debug: false,
            ...flags,
        };
    }

    getTrackingSessionUuid() {
        return this.transport.sessionStorage.read();
    }

    updateTrackingSessionUuid(value) {
        return this.transport.sessionStorage.write(value);
    }

    trackPageView(input) {
        return this.track('PageView', {
            page_type: input.pageType,
            title: input.title,
        }, input.navigationId || `page:${input.pageType}:${input.pageUrl || this.locationProvider()}`, input);
    }

    trackViewCategory(input) {
        return this.track('ViewCategory', {
            category_id: input.categoryId,
            page_context: input.pageContext,
            filters: sanitizeFilters(input.filters),
            sort: input.sort,
        }, input.pageOccurrenceId || `category:${input.categoryId}:${input.pageUrl || this.locationProvider()}`, input);
    }

    trackViewContent(input) {
        return this.track('ViewContent', {
            product_id: input.productId,
            product_option_id: input.productOptionId,
            page_context: input.pageContext,
        }, input.pageOccurrenceId || `product:${input.productId}:${input.pageUrl || this.locationProvider()}`, input);
    }

    trackSearch(input) {
        const keyword = normalizeSearchKeyword(input.keyword);

        return this.track('Search', {
            keyword,
            result_count: input.resultCount,
            filters: sanitizeFilters(input.filters),
            sort: input.sort,
            page: input.page,
        }, input.searchRequestId || `search:${keyword}:${input.pageUrl || this.locationProvider()}`, input);
    }

    trackOrder(input = {}) {
        const orderId = Number(input.orderId);

        if (!Number.isInteger(orderId) || orderId <= 0) {
            return Promise.resolve(null);
        }

        const occurrenceId =
            input.interactionId
            || input.screenOccurrenceId
            || this.uuidGenerator();

        return this.track(
            'TrackOrder',
            {
                order_id: orderId,
                entry_point:
                    input.entryPoint || 'order_detail',
                action:
                    input.action || 'view_tracking',
                tracking_available:
                    Boolean(input.trackingAvailable),
                courier_code:
                    input.courierCode || null,
                tracking_status:
                    input.trackingStatus || null,
            },
            occurrenceId,
            input
        );
    }

    trackSelectCoupon(input) {
        return this.track('SelectCoupon', {
            coupon_id: input.couponId,
            coupon_code: input.couponCode,
            coupon_scope: input.couponScope,
            coupon_benefit_type: input.couponBenefitType,
            shop_id: input.shopId,
            action: input.action,
            selection_status: input.selectionStatus,
            validation_success: input.validationSuccess,
            entry_point: input.entryPoint,
            discount_preview: input.discountPreview,
            currency: input.currency || 'THB',
        }, input.interactionId || this.uuidGenerator(), input);
    }

    track(eventType, properties, occurrenceKey, input = {}) {
        if (
            !this.flags.enabled
            || !this.flags.sessionPersistence
            || !this.flags[eventType]
        ) {
            return Promise.resolve({ ok: true, status: 'client_disabled' });
        }

        const key = `${eventType}:${occurrenceKey}`;
        if (this.occurrences.has(key)) {
            return this.occurrences.get(key).promise;
        }

        let clientEventId;
        try {
            clientEventId = (input.clientEventId || this.uuidGenerator()).toLowerCase();
        } catch (error) {
            return Promise.resolve({ ok: false, status: 'uuid_unavailable' });
        }

        if (!isUuidV4(clientEventId)) {
            return Promise.resolve({ ok: false, status: 'invalid_client_event_id' });
        }

        let pageUrl;
        let referrerUrl;
        try {
            const rawPageUrl = input.pageUrl || this.locationProvider();
            pageUrl = sanitizeUrl(rawPageUrl, rawPageUrl);
            const rawReferrer = input.referrerUrl === undefined
                ? this.referrerProvider()
                : input.referrerUrl;
            referrerUrl = rawReferrer
                ? sanitizeUrl(rawReferrer, rawPageUrl)
                : undefined;
        } catch (error) {
            return Promise.resolve({ ok: false, status: 'invalid_url' });
        }

        const occurredAt = input.occurredAt
            ? new Date(input.occurredAt)
            : this.clock();
        if (!(occurredAt instanceof Date) || Number.isNaN(occurredAt.getTime())) {
            return Promise.resolve({ ok: false, status: 'invalid_occurred_at' });
        }

        const payload = {
            event_type: eventType,
            client_event_id: clientEventId,
            occurred_at: occurredAt.toISOString(),
            page_url: pageUrl,
            referrer_url: referrerUrl,
            source: this.source,
            properties: compact(properties),
            context: this.context,
            schema_version: SCHEMA_VERSION,
        };

        const promise = this.transport.send(payload)
            .then((result) => {
                if (!result.ok && this.flags.debug) {
                    this.debug({
                        eventType,
                        status: result.status,
                        httpStatus: result.httpStatus,
                    });
                }

                return result;
            })
            .catch(() => ({ ok: false, status: 'client_failure' }));

        this.occurrences.set(key, { clientEventId, promise });
        this.pruneOccurrences();

        return promise;
    }

    pruneOccurrences() {
        while (this.occurrences.size > this.maxOccurrences) {
            this.occurrences.delete(this.occurrences.keys().next().value);
        }
    }
}

module.exports = {
    ALLOWED_QUERY_PARAMETERS,
    REQUEST_ID_HEADER,
    SCHEMA_VERSION,
    SESSION_HEADER,
    ANONYMOUS_HEADER,
    STORAGE_KEY,
    ANONYMOUS_STORAGE_KEY,
    TrackingClient,
    TrackingTransport,
    WebTrackingSessionStorage,
    defaultUuidGenerator,
    isUuidV4,
    normalizeSearchKeyword,
    sanitizeFilters,
    sanitizeUrl,
};
